import { Button } from "@hcsc-fiber/react";
import axios, { AxiosError } from "axios";
import React, { useContext, useState } from "react";
import { consentsUrl } from "../constants/urls";
import { StateContext } from "../context/stateContext";
import styles from "../styles/third-party.module.scss";
import { translations } from "../translate";
import DisconnectConsentDialog from "./DisconnectConsentDialog/DisconnectConsentDialog";

interface IDisconnectButton {
  buttonTitle: string;
  dialogTitle: string;
  thirdPartyConsents: string[];
  disabled: boolean;
  onDisconnect: () => void;
}

declare type ErrorType = { message: string; code: string };

interface ErrorResponse {
  errors: ErrorType[];
}

const DisconnectButton: React.FC<IDisconnectButton> = ({
  thirdPartyConsents,
  disabled,
  onDisconnect,
  dialogTitle,
  buttonTitle,
}) => {
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [disconnectErrors, setDisconnectErrors] = useState<ErrorType | null>(null);
  const { cidmUrl, accessToken, languageCode } = useContext(StateContext);

  const reqConfig = {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    withCredentials: true,
  };

  const onCloseDisconnectConsentDialog = () => {
    setDisconnectErrors(null);
    setIsDisconnectOpen(false);
  };

  const consentUrl = cidmUrl + consentsUrl;

  const onDisconnectConsents = async (password: string) => {
    setDisconnectErrors(null);
    try {
      await axios.post(`${consentUrl}/revoke-consent`, { password, hcscAppConsent: thirdPartyConsents }, reqConfig);
      onCloseDisconnectConsentDialog();

      onDisconnect();
    } catch (e) {
      const err = e as AxiosError<ErrorResponse>;
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        if (err.response.data.errors[0].code === "INVALID_CREDENTIALS") {
          setDisconnectErrors({
            message: translations.incorrectPassword[languageCode],
            code: "INVALID_CREDENTIALS",
          });
        } else {
          setDisconnectErrors(err.response.data.errors[0]);
        }
      } else {
        setDisconnectErrors({ message: translations.unknownError[languageCode], code: "UNKNOWN_ERROR" });
      }
    }
  };

  return (
    <div className={styles.buttonContainer}>
      <Button
        kind="tertiary"
        onClick={() => setIsDisconnectOpen(true)}
        disabled={!thirdPartyConsents.length || disabled}
      >
        {buttonTitle}
      </Button>
      <DisconnectConsentDialog
        submitButtonTitle={buttonTitle}
        title={dialogTitle}
        isOpen={isDisconnectOpen}
        onClose={onCloseDisconnectConsentDialog}
        onSubmit={onDisconnectConsents}
        error={disconnectErrors}
        clearError={() => setDisconnectErrors(null)}
        setDisconnectErrors={setDisconnectErrors}
      />
    </div>
  );
};

export default DisconnectButton;
